import React, { useState, useMemo } from 'react'
import { cn } from '@/lib/utils'
import { paceBand, paceComparison } from '@/lib/plain-language'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from 'recharts'

type Preset = 'conservative' | 'balanced' | 'aggressive'

const PRESETS: Record<Preset, Array<[number, number]>> = {
  conservative: [[0, -12], [0.3, -8], [0.5, -5], [0.7, -3], [1, 0], [1.3, 4], [1.5, 7], [2, 12], [3, 18], [4, 22]],
  balanced: [[0, -20], [0.3, -14], [0.5, -9], [0.7, -5], [1, 0], [1.3, 7], [1.5, 12], [2, 22], [3, 35], [4, 45]],
  aggressive: [[0, -30], [0.3, -22], [0.5, -15], [0.7, -8], [1, 0], [1.3, 10], [1.5, 18], [2, 35], [3, 55], [4, 70]],
}

const PRESET_OPTIONS: { value: Preset; label: string }[] = [
  { value: 'conservative', label: 'Conservative' },
  { value: 'balanced', label: 'Balanced' },
  { value: 'aggressive', label: 'Aggressive' },
]

const BASE_RATE = 100
const BASELINE_OCCUPANCY = 65 // expected occupancy % — matches the occupancy fallback neutral point

/** Linear interpolation between preset curve points */
function interpolate(points: Array<[number, number]>, x: number): number {
  if (x <= points[0][0]) return points[0][1]
  if (x >= points[points.length - 1][0]) return points[points.length - 1][1]

  for (let i = 0; i < points.length - 1; i++) {
    const [x0, y0] = points[i]
    const [x1, y1] = points[i + 1]
    if (x >= x0 && x <= x1) {
      const t = (x - x0) / (x1 - x0)
      return y0 + t * (y1 - y0)
    }
  }
  return 0
}

function buildCurveData(preset: Preset) {
  const points = PRESETS[preset]
  const data: Array<{ ratio: number; modifier: number }> = []
  // Generate smooth curve with 81 points from 0 to 4
  for (let i = 0; i <= 80; i++) {
    const ratio = (i / 80) * 4
    data.push({
      ratio: Math.round(ratio * 100) / 100,
      modifier: Math.round(interpolate(points, ratio) * 10) / 10,
    })
  }
  return data
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null
  const d = payload[0].payload
  const band = paceBand(d.ratio)
  const occ = Math.round(d.ratio * BASELINE_OCCUPANCY)
  return (
    <div className="rounded-lg border border-border/60 bg-white px-3 py-2 shadow-lg shadow-black/5">
      <p className="text-[11px] font-medium text-foreground">{band.label}</p>
      <p className="text-[11px] text-muted-foreground">{paceComparison(occ, BASELINE_OCCUPANCY)}</p>
      <p className="mt-1 text-sm font-semibold text-foreground tabular-nums">
        {d.modifier >= 0 ? '+' : ''}{d.modifier.toFixed(1)}%
      </p>
    </div>
  )
}

const URGENCY_TABLE: Array<[number, number]> = [[0, 1.8], [1, 1.8], [3, 1.6], [7, 1.3], [14, 1.0], [30, 0.7], [60, 0.5]]

function urgencyMultiplier(days: number): number {
  return interpolate(URGENCY_TABLE, days)
}

export default function SensitivityExplorer() {
  const [preset, setPreset] = useState<Preset>('balanced')
  const [occupancy, setOccupancy] = useState(60)
  const [showUrgency, setShowUrgency] = useState(false)
  const [urgencyLeadTime, setUrgencyLeadTime] = useState(14)

  const curveData = useMemo(() => buildCurveData(preset), [preset])

  const paceRatio = Math.min(occupancy / BASELINE_OCCUPANCY, 4)
  const modifier = interpolate(PRESETS[preset], paceRatio)
  const adjustedRate = Math.round((BASE_RATE * (1 + modifier / 100)) * 100) / 100

  return (
    <div className="space-y-5">
      {/* Preset selector — segmented control */}
      <div className="inline-flex rounded-lg bg-slate-100 p-1">
        {PRESET_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            onClick={() => setPreset(opt.value)}
            className={cn(
              'rounded-md px-4 py-1.5 text-xs font-medium transition-all duration-200 cursor-pointer',
              preset === opt.value
                ? 'bg-white text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Chart */}
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={curveData} margin={{ top: 10, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
            <XAxis
              dataKey="ratio"
              type="number"
              domain={[0, 4]}
              ticks={[0, 0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4]}
              tickFormatter={(v: number) => `${Math.round(v * 100)}%`}
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={{ stroke: '#e2e8f0' }}
              tickLine={false}
            />
            <YAxis
              tickFormatter={(v: number) => `${v}%`}
              tick={{ fontSize: 11, fill: '#64748b' }}
              axisLine={false}
              tickLine={false}
              width={45}
            />
            <ReferenceLine y={0} stroke="#94a3b8" strokeDasharray="4 4" />
            <ReferenceLine x={1} stroke="#94a3b8" strokeDasharray="4 4" />
            {/* Indicator for current occupancy */}
            <ReferenceLine
              x={paceRatio}
              stroke="#0d9488"
              strokeWidth={1.5}
              strokeDasharray="3 3"
            />
            <RechartsTooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="modifier"
              stroke="#0d9488"
              strokeWidth={2.5}
              dot={false}
              animationDuration={500}
              animationEasing="ease-out"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* X-axis labels */}
      <div className="flex justify-between px-4 -mt-2">
        <span className="text-[10px] text-muted-foreground">Behind</span>
        <span className="text-[10px] text-muted-foreground font-medium">On track · usual pace</span>
        <span className="text-[10px] text-muted-foreground">Ahead</span>
      </div>

      {/* Occupancy slider */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-xs font-medium text-foreground">
            Occupancy scenario
          </label>
          <span className="text-sm font-semibold tabular-nums text-foreground">{occupancy}%</span>
        </div>
        <input
          type="range"
          min={0}
          max={100}
          value={occupancy}
          onChange={(e) => setOccupancy(Number(e.target.value))}
          className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-200 accent-primary
                     [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4
                     [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:shadow-md
                     [&::-webkit-slider-thumb]:transition-transform [&::-webkit-slider-thumb]:hover:scale-110"
        />
        <div className="flex justify-between text-[10px] text-muted-foreground tabular-nums">
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Urgency toggle */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-xs font-medium text-foreground cursor-pointer">
          <input
            type="checkbox"
            checked={showUrgency}
            onChange={(e) => setShowUrgency(e.target.checked)}
            className="rounded accent-primary cursor-pointer"
          />
          Show with booking urgency
        </label>
        {showUrgency && (
          <select
            value={urgencyLeadTime}
            onChange={(e) => setUrgencyLeadTime(Number(e.target.value))}
            className="rounded-lg border border-border/60 bg-white px-2 py-1.5 text-xs text-foreground shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40 cursor-pointer"
          >
            <option value={0}>Tomorrow (0d)</option>
            <option value={3}>3 days</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days (baseline)</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
          </select>
        )}
      </div>

      {/* Result callout */}
      <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 space-y-2">
        <p className="text-xs text-muted-foreground leading-relaxed">
          At <span className="font-semibold text-foreground">{occupancy}%</span> full against a usual{' '}
          <span className="tabular-nums">{BASELINE_OCCUPANCY}%</span> by now, booking pace reads{' '}
          <span className="font-semibold text-foreground">{paceBand(paceRatio).label}</span>
        </p>
        {showUrgency ? (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1">
            <span className="text-[11px] text-muted-foreground">Base adjustment</span>
            <span
              className={cn(
                'text-[11px] font-semibold tabular-nums',
                modifier > 0 ? 'text-emerald-600' : modifier < 0 ? 'text-rose-600' : 'text-foreground'
              )}
            >
              {modifier >= 0 ? '+' : ''}{modifier.toFixed(1)}%
            </span>
            <span className="text-[11px] text-muted-foreground">Urgency-adjusted</span>
            <span
              className={cn(
                'text-[11px] font-semibold tabular-nums',
                modifier * urgencyMultiplier(urgencyLeadTime) > 0
                  ? 'text-emerald-600'
                  : modifier * urgencyMultiplier(urgencyLeadTime) < 0
                    ? 'text-rose-600'
                    : 'text-foreground'
              )}
            >
              {(modifier * urgencyMultiplier(urgencyLeadTime)) >= 0 ? '+' : ''}
              {(modifier * urgencyMultiplier(urgencyLeadTime)).toFixed(1)}%
            </span>
            <span className="text-[11px] text-muted-foreground">£{BASE_RATE} base &rarr;</span>
            <span className="text-sm font-bold tabular-nums text-foreground tracking-tight">
              £{(Math.round((BASE_RATE * (1 + (modifier * urgencyMultiplier(urgencyLeadTime)) / 100)) * 100) / 100).toFixed(2)}
            </span>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground leading-relaxed">
              &rarr; rate adjusts by{' '}
              <span
                className={cn(
                  'font-semibold tabular-nums',
                  modifier > 0 ? 'text-emerald-600' : modifier < 0 ? 'text-rose-600' : 'text-foreground'
                )}
              >
                {modifier >= 0 ? '+' : ''}{modifier.toFixed(1)}%
              </span>
            </p>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                £{BASE_RATE} base
              </span>
              <span className="text-muted-foreground">&rarr;</span>
              <span className="text-lg font-bold tabular-nums text-foreground tracking-tight">
                £{adjustedRate.toFixed(2)}
              </span>
            </div>
            <p className="text-[10px] text-muted-foreground/60 leading-relaxed">
              Shown at 14-day lead time (baseline). Enable booking urgency above to see how adjustments change closer to or further from check-in.
            </p>
          </>
        )}
      </div>
    </div>
  )
}
